/**
 * The 1,062-row endpoint table.
 *
 * Three decisions shape this file.
 *
 * **One request, not a pager.** `GET /api/v1/endpoints` returns a row for every
 * endpoint in the registry, and the backend caps `limit` at 2000, so the whole
 * window fits in a single response (~200KB). Paging would have meant the status
 * filter and the family grouping only ever saw one page at a time, which turns
 * "show me everything that is down" into "show me everything that is down on
 * page 3".
 *
 * **Virtualized, not paginated.** 1,062 rows x ~14 cells is ~15,000 DOM nodes;
 * a virtualizer keeps ~20 rows alive at 44px each. Every row is exactly
 * `ROW_HEIGHT` tall, so the virtualizer never has to measure anything.
 *
 * **Grouped by family, worst first.** The registry's 53 tags are the unit an
 * outage actually happens in. Families that contain a `down` or `degraded`
 * endpoint start expanded and sort to the top; the quiet ones collapse to a
 * single line so 53 headers, not 1,062 rows, is what you land on.
 *
 * TanStack Table owns the column model and the sort; TanStack Virtual owns the
 * scroll geometry; the markup is ours (`EndpointRow`), because a virtualized
 * `<table>` with absolutely-positioned `<tr>`s is invalid HTML that reflows
 * badly. That split is exactly what the headless libraries are for.
 */
import { useQuery } from '@tanstack/react-query'
import {
  createColumnHelper,
  createSortedRowModel,
  rowSortingFeature,
  tableFeatures,
  useTable,
} from '@tanstack/react-table'
import type { SortFn, SortingState } from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Fragment, useCallback, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { useI18n } from '../i18n'
import type { TFunction } from '../i18n'
import { ApiError, api } from '../api/client'
import { WINDOW_REFRESH_MS } from '../api/types'
import type { EndpointListResponse, EndpointSummary, HealthState, WindowId } from '../api/types'
import { FreshnessBadge } from './FreshnessBadge'
import { ENDPOINT_GRID, EndpointRow, ROW_HEIGHT } from './EndpointRow'
import { FamilyGroup, emptyHealthCounts } from './FamilyGroup'
import type { FamilyBucket } from './FamilyGroup'
import { STATUS_COLUMNS, columnTotals, numberFormat } from './statusColors'
import type { StatusColumn } from './statusColors'

/** All five buckets at zero — the starting point once a family has any data. */
export function emptyStatusTotals(): Record<StatusColumn, number> {
  return Object.fromEntries(STATUS_COLUMNS.map((c) => [c, 0])) as Record<StatusColumn, number>
}

/** The backend's hard ceiling (`limit: int = Query(200, ge=1, le=2000)`), which
 *  comfortably covers the 1,062-endpoint registry in one round trip. */
export const PAGE_LIMIT = 2000

/** Rows kept alive above and below the viewport. */
export const OVERSCAN = 10

/**
 * Sort tier for the health states.
 *
 * `unknown` deliberately outranks `healthy`: it means "we have no idea", and an
 * endpoint we know nothing about is a more interesting thing to look at than
 * one we have positively confirmed is fine. (The backend's own ordering in
 * `app/routes/metrics.py` puts `unknown` last instead — it has to, because it
 * truncates to a 200-row page and ~1,058 unknowns would bury every endpoint
 * that carries traffic. Here nothing is truncated, so the honest order wins.)
 */
export const HEALTH_TIER: Record<HealthState, number> = {
  down: 0,
  degraded: 1,
  unknown: 2,
  healthy: 3,
}

/** Families in one of these states are opened for you; you did not come here to
 *  click 53 triangles looking for the broken one. */
const PROBLEM_STATES: ReadonlySet<HealthState> = new Set<HealthState>(['down', 'degraded'])

const features = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
})

const helper = createColumnHelper<typeof features, EndpointSummary>()

/** Ascending only — Table reverses it for `desc`. */
const byHealth: SortFn<typeof features, EndpointSummary> = (a, b) =>
  HEALTH_TIER[a.original.health] - HEALTH_TIER[b.original.health]

/** `null` (no data) sorts below a real 0, so descending puts it last. */
const byTotal: SortFn<typeof features, EndpointSummary> = (a, b) =>
  (a.original.total ?? -1) - (b.original.total ?? -1)

const byPath: SortFn<typeof features, EndpointSummary> = (a, b) =>
  a.original.path.localeCompare(b.original.path)

/**
 * The column model, built per language.
 *
 * The headers are the one part of the table config that is copy, so the model
 * is a function of `t` and is memoised on it in the component below: `t` only
 * changes when the language does, so the columns are as stable as they were
 * when they were a module constant, and a language switch redraws the header
 * row without touching the sort state.
 *
 * The five bucket headers stay `BUCKET_STYLES[bucket].label` — `2xx`, `429`
 * and friends are protocol tokens, identical in every language.
 */
function buildColumns(t: TFunction) {
  return helper.columns([
    helper.accessor('health', { header: t('domain.health'), sortFn: byHealth }),
    helper.accessor((row) => row.path, {
      id: 'endpoint',
      header: t('domain.endpoint'),
      sortFn: byPath,
    }),
    helper.accessor('total', { header: t('domain.requests'), sortFn: byTotal, sortDescFirst: true }),
    ...STATUS_COLUMNS.map((column) =>
      helper.display({ id: column, header: column, enableSorting: false }),
    ),
    helper.display({ id: 'ratio', header: t('dashboard.table.colMix'), enableSorting: false }),
    // No Errors column. The error rate it showed is already carried by the Mix
    // bar and the 5xx column beside it, and on this zone 5xx is 0.086% of
    // traffic — a column that reads "0.0%" on virtually every one of 1,062
    // rows is width spent on a constant.
    helper.display({ id: 'trend', header: t('dashboard.table.colTrend'), enableSorting: false }),
  ])
}

/** Worst state first, then the endpoints that see the most traffic. */
const DEFAULT_SORTING: SortingState = [
  { id: 'health', desc: false },
  { id: 'total', desc: true },
]

export interface FamilyBucketWithRows extends FamilyBucket {
  /** Only the rows the status filter leaves visible, in table order. */
  rows: EndpointSummary[]
}

/**
 * Fold an ordered endpoint list into family sections.
 *
 * Counts and totals are computed over **every** endpoint in the family, even
 * the ones `isVisible` rejects: a health count that changed when you toggled a
 * filter chip would be describing the filter rather than the API. Only `rows`
 * and `visibleCount` respond to the filter, and a family the filter empties
 * out is dropped rather than left as a header over nothing.
 */
export function groupByFamily(
  endpoints: readonly EndpointSummary[],
  isVisible: (ep: EndpointSummary) => boolean = () => true,
): FamilyBucketWithRows[] {
  const buckets = new Map<string, FamilyBucketWithRows>()

  for (const ep of endpoints) {
    let bucket = buckets.get(ep.family)
    if (!bucket) {
      bucket = {
        family: ep.family,
        rows: [],
        visibleCount: 0,
        endpointCount: 0,
        measuredCount: 0,
        // Starts as null, not 0: until some endpoint returns a number there is
        // nothing to sum, and 0 would be a measurement we never took.
        totalRequests: null,
        statusTotals: null,
        healthCounts: emptyHealthCounts(),
        worstHealth: 'healthy',
      }
      buckets.set(ep.family, bucket)
    }
    bucket.endpointCount += 1
    if (ep.total !== null) {
      bucket.measuredCount += 1
      bucket.totalRequests = (bucket.totalRequests ?? 0) + ep.total
      // Fold the endpoint's status codes into the family's bucket columns, so a
      // collapsed family answers "what is this family made of" without being
      // opened. Allocated lazily for the same reason totalRequests starts null.
      bucket.statusTotals ??= emptyStatusTotals()
      for (const [column, count] of Object.entries(columnTotals(ep.by_status))) {
        bucket.statusTotals[column as StatusColumn] += count
      }
    }
    bucket.healthCounts[ep.health] += 1
    if (HEALTH_TIER[ep.health] < HEALTH_TIER[bucket.worstHealth]) bucket.worstHealth = ep.health
    if (isVisible(ep)) {
      bucket.rows.push(ep)
      bucket.visibleCount += 1
    }
  }

  return (
    [...buckets.values()]
      .filter((b) => b.rows.length > 0)
      // Alphabetical, not worst-health-first. With 53 families the list is a
      // directory you look things up in, and a health-ranked order moves a
      // family every time its traffic shifts — you cannot learn where anything
      // lives. Severity still ranks the ENDPOINT rows inside each family, and
      // the family header carries its own health badge, so nothing is lost.
      .sort((a, b) => a.family.localeCompare(b.family))
  )
}

type FlatItem =
  | { kind: 'family'; key: string; bucket: FamilyBucketWithRows }
  | { kind: 'endpoint'; key: string; endpoint: EndpointSummary }

export function endpointsQueryKey(window: WindowId) {
  return ['endpoints', window] as const
}

export interface EndpointTableProps {
  window: WindowId
  /** Health states to show. Filtered here, not upstream: the API's `health=`
   *  parameter takes one state and the point of the filter is to ask for
   *  "down and degraded" as a single view. */
  statusFilter: ReadonlySet<HealthState>
  /** Wired to the Task 23 drill-down. */
  onSelectEndpoint?: (endpointId: string) => void
  /** The row whose detail panel is open, so it can be marked in the list.
   *  With the panel beside the table rather than above it, nothing else tells
   *  you which of 1,062 rows you are looking at. */
  selectedEndpointId?: string | null
}

const NO_ENDPOINTS: EndpointSummary[] = []

export function EndpointTable({
  window,
  statusFilter,
  onSelectEndpoint,
  selectedEndpointId = null,
}: EndpointTableProps) {
  const { t } = useI18n()
  const columns = useMemo(() => buildColumns(t), [t])

  const { data, error, isPending } = useQuery({
    queryKey: endpointsQueryKey(window),
    queryFn: () =>
      api.get<EndpointListResponse>('/api/v1/endpoints', { window, limit: PAGE_LIMIT }),
    // Matched to the server's per-window cache TTL: an unattended dashboard has
    // to keep up with the data, and asking faster than the cache turns over
    // would only re-download the same 200KB.
    staleTime: WINDOW_REFRESH_MS[window],
    refetchInterval: WINDOW_REFRESH_MS[window],
    refetchOnWindowFocus: true,
  })

  // A fresh `[]` every render would invalidate the sorted row model each time.
  const rows = data?.items ?? NO_ENDPOINTS

  const table = useTable({
    features,
    columns,
    data: rows,
    initialState: { sorting: DEFAULT_SORTING },
    enableMultiSort: true,
    // The table always has an order; "unsorted" is not a useful third state.
    enableSortingRemoval: false,
  })

  const [search, setSearch] = useState('')
  const needle = search.trim().toLowerCase()

  const sorted = table.getRowModel().rows
  const buckets = useMemo(
    () =>
      groupByFamily(
        sorted.map((r) => r.original),
        // Path AND family, because "douyin" is as likely to mean the family as
        // a path fragment, and matching only one of them makes the box feel
        // broken for the other.
        (ep) =>
          statusFilter.has(ep.health) &&
          (needle === '' ||
            ep.path.toLowerCase().includes(needle) ||
            ep.family.toLowerCase().includes(needle)),
      ),
    [sorted, statusFilter, needle],
  )

  const autoExpanded = useMemo(
    () => new Set(buckets.filter((b) => PROBLEM_STATES.has(b.worstHealth)).map((b) => b.family)),
    [buckets],
  )
  // `null` means "nobody has touched this yet", so the auto-expansion can keep
  // following the data. The first click freezes it into an explicit set.
  const [override, setOverride] = useState<ReadonlySet<string> | null>(null)
  // While searching, show every surviving family open. `groupByFamily` has
  // already dropped the families with no match, so what is left is exactly the
  // result set — leaving those collapsed would hide the very rows you searched
  // for behind 53 closed triangles.
  const expanded = needle !== '' ? new Set(buckets.map((b) => b.family)) : (override ?? autoExpanded)

  const toggle = useCallback(
    (family: string) =>
      setOverride((prev) => {
        const next = new Set(prev ?? autoExpanded)
        if (!next.delete(family)) next.add(family)
        return next
      }),
    [autoExpanded],
  )

  const flat = useMemo<FlatItem[]>(() => {
    const items: FlatItem[] = []
    for (const bucket of buckets) {
      items.push({ kind: 'family', key: `f:${bucket.family}`, bucket })
      if (!expanded.has(bucket.family)) continue
      for (const ep of bucket.rows)
        items.push({ kind: 'endpoint', key: `e:${ep.endpoint_id}`, endpoint: ep })
    }
    return items
  }, [buckets, expanded])

  const scrollRef = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: flat.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    getItemKey: (index) => flat[index].key,
    overscan: OVERSCAN,
  })

  if (isPending) return <TableSkeleton t={t} />
  if (error) return <TableError error={error} t={t} />

  const visibleRows = buckets.reduce((sum, b) => sum + b.visibleCount, 0)

  return (
    <section aria-label={t('dashboard.table.aria')} className="space-y-2">
      <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div className="text-xs text-muted">
          <Interpolated
            template={t('dashboard.table.summary')}
            values={{
              visible: <span className="num text-fg">{numberFormat.format(visibleRows)}</span>,
              total: <span className="num">{numberFormat.format(data.total_count)}</span>,
              families: <span className="num text-fg">{numberFormat.format(buckets.length)}</span>,
            }}
          />
        </div>
        <div className="flex items-center gap-3">
          <label className="relative">
            <span className="sr-only">{t('dashboard.table.searchLabel')}</span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('dashboard.table.searchPlaceholder')}
              className="w-64 rounded border border-border bg-surface px-2 py-1 text-xs text-fg
                         placeholder:text-muted focus:border-accent focus:outline-none"
            />
          </label>
          <button
            type="button"
            onClick={() => setOverride(new Set(buckets.map((b) => b.family)))}
            className="rounded border border-border px-1.5 py-0.5 text-xs text-muted hover:text-fg"
          >
            {t('dashboard.table.expandAll')}
          </button>
          <button
            type="button"
            onClick={() => setOverride(new Set())}
            className="rounded border border-border px-1.5 py-0.5 text-xs text-muted hover:text-fg"
          >
            {t('dashboard.table.collapseAll')}
          </button>
          <FreshnessBadge meta={data.meta} />
        </div>
      </header>

      {flat.length === 0 ? (
        <p role="status" className="rounded-md border border-border bg-hatch px-3 py-6 text-center text-sm text-muted">
          {needle === ''
            ? t('dashboard.table.emptyFiltered')
            : t('dashboard.table.emptySearch', { query: search.trim() })}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <div className="min-w-[68rem]">
            <div role="grid" aria-label={t('dashboard.table.aria')} aria-rowcount={flat.length}>
              <HeaderRow table={table} t={t} />
              <div
                ref={scrollRef}
                data-testid="endpoint-scroll"
                className="h-[min(78vh,62rem)] overflow-y-auto"
              >
                <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
                  {virtualizer.getVirtualItems().map((item) => {
                    const entry = flat[item.index]
                    const style = {
                      position: 'absolute' as const,
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${item.start}px)`,
                    }
                    return entry.kind === 'family' ? (
                      <FamilyGroup
                        key={entry.key}
                        bucket={entry.bucket}
                        expanded={expanded.has(entry.bucket.family)}
                        onToggle={toggle}
                        style={style}
                      />
                    ) : (
                      <EndpointRow
                        key={entry.key}
                        endpoint={entry.endpoint}
                        window={window}
                        onSelect={onSelectEndpoint}
                        selected={entry.endpoint.endpoint_id === selectedEndpointId}
                        style={style}
                      />
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

/* ------------------------------------------------------------------ */

function HeaderRow({
  table,
  t,
}: {
  table: ReturnType<typeof useTable<typeof features, EndpointSummary>>
  t: TFunction
}) {
  return (
    <div className="sticky top-0 z-10 border-b border-border bg-bg">
      {table.getHeaderGroups().map((group) => (
        <div
          key={group.id}
          role="row"
          data-testid="header-row"
          className={`${ENDPOINT_GRID} h-9 text-xs font-medium text-muted`}
        >
          {group.headers.map((header) => {
            const sortable = header.column.getCanSort()
            const direction = header.column.getIsSorted()
            const numeric = header.column.id !== 'health' && header.column.id !== 'endpoint'
            return (
              <div
                key={header.id}
                role="columnheader"
                aria-sort={
                  direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none'
                }
                className={numeric ? 'text-right' : 'text-left'}
              >
                {sortable ? (
                  <button
                    type="button"
                    onClick={header.column.getToggleSortingHandler()}
                    title={t('dashboard.table.sortHint')}
                    className="hover:text-fg"
                  >
                    <table.FlexRender header={header} />
                    {direction && <span aria-hidden="true">{direction === 'asc' ? ' ▲' : ' ▼'}</span>}
                  </button>
                ) : (
                  <table.FlexRender header={header} />
                )}
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

function TableSkeleton({ t }: { t: TFunction }) {
  return (
    <section aria-label={t('dashboard.table.aria')} aria-busy="true" className="space-y-2">
      <div className="h-4 w-56 animate-pulse rounded bg-border" />
      <div className="space-y-1 rounded-md border border-border p-2">
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className="h-9 animate-pulse rounded bg-border" />
        ))}
      </div>
    </section>
  )
}

/** A failed list shows the failure. The API only 503s when it has no snapshot
 *  at all; anything it can serve comes back with `degraded: true` instead. */
function TableError({ error, t }: { error: unknown; t: TFunction }) {
  const detail =
    error instanceof ApiError
      ? error.detail || t('dashboard.table.errorHttp', { status: error.status })
      : t('dashboard.table.errorUnreachable')
  return (
    <section aria-label={t('dashboard.table.aria')}>
      <p role="alert" className="rounded-md bg-down/10 px-3 py-2 text-sm text-down">
        {t('dashboard.table.errorTitle', { detail })}
      </p>
    </section>
  )
}

/**
 * Render a translated sentence whose placeholders are nodes, not strings.
 *
 * The summary line sets its three counts in `.num` (mono, tabular) and brightens
 * two of them, which a single interpolated string cannot carry — and splitting
 * the sentence into keyed fragments would force Chinese into English word order.
 * So the key holds the whole sentence, `t()` hands back the template with its
 * placeholders intact, and the placeholders are swapped for the styled spans
 * here. Chinese reorders them freely; the styling follows each count.
 */
const PLACEHOLDER = /(\{\{\s*\w+\s*\}\})/g

function Interpolated({
  template,
  values,
}: {
  template: string
  values: Record<string, ReactNode>
}) {
  return (
    <>
      {template.split(PLACEHOLDER).map((part, i) => {
        const name = /^\{\{\s*(\w+)\s*\}\}$/.exec(part)?.[1]
        // An unknown placeholder renders as itself, matching the runtime's own
        // behaviour: a typo should be visible, not silently blank.
        return <Fragment key={i}>{name === undefined ? part : (values[name] ?? part)}</Fragment>
      })}
    </>
  )
}
